import React, { useState } from 'react';
import {
  Chip,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import contratoVentaService, { ContratoPeriodo } from '../../../services/contratoVentaService';
import { formatCurrency } from '../../../utils/formatters';

interface ContratoPeriodosTabProps {
  contratoId: number;
  periodos: ContratoPeriodo[];
  onUpdated: (periodos: ContratoPeriodo[]) => void;
  readOnly?: boolean;
}

const estadoColor = (estado: string): 'default' | 'success' | 'warning' | 'error' => {
  if (estado === 'Facturado') return 'success';
  if (estado === 'Pendiente') return 'warning';
  if (estado === 'ParcialmenteDevuelto') return 'error';
  return 'default';
};

const ContratoPeriodosTab: React.FC<ContratoPeriodosTabProps> = ({
  contratoId,
  periodos,
  onUpdated,
  readOnly,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [editingOc, setEditingOc] = useState<Record<number, string>>({});
  const [savingId, setSavingId] = useState<number | null>(null);

  const handleSaveOc = async (periodo: ContratoPeriodo) => {
    try {
      setSavingId(periodo.id);
      const updated = await contratoVentaService.updatePeriodo(
        contratoId,
        periodo.id,
        editingOc[periodo.id] ?? periodo.nroOrdenCompra ?? undefined
      );
      onUpdated(periodos.map((p) => (p.id === periodo.id ? { ...p, ...updated } : p)));
      enqueueSnackbar('OC del período actualizada', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.message || 'Error al guardar OC', { variant: 'error' });
    } finally {
      setSavingId(null);
    }
  };

  if (periodos.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Todavía no hay períodos generados. Guardá el contrato para generar las cuotas.
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Cuota</TableCell>
          <TableCell>Desde</TableCell>
          <TableCell>Hasta</TableCell>
          <TableCell>Facturación</TableCell>
          <TableCell>OC</TableCell>
          <TableCell>Estado</TableCell>
          <TableCell align="right">Esperado</TableCell>
          <TableCell align="right">Facturado</TableCell>
          <TableCell align="right">Pendiente</TableCell>
          <TableCell>Factura</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {periodos.map((p) => (
          <TableRow key={p.id}>
            <TableCell>{p.nroCuota}</TableCell>
            <TableCell>{String(p.fechaDesde).slice(0, 10)}</TableCell>
            <TableCell>{String(p.fechaHasta).slice(0, 10)}</TableCell>
            <TableCell>{String(p.fechaFacturacion).slice(0, 10)}</TableCell>
            <TableCell>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <TextField
                  size="small"
                  value={editingOc[p.id] ?? p.nroOrdenCompra ?? ''}
                  onChange={(e) => setEditingOc((prev) => ({ ...prev, [p.id]: e.target.value }))}
                  disabled={readOnly}
                  sx={{ width: 120 }}
                />
                {!readOnly && (
                  <Tooltip title="Guardar OC">
                    <span>
                      <IconButton
                        size="small"
                        disabled={savingId === p.id}
                        onClick={() => handleSaveOc(p)}
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            </TableCell>
            <TableCell>
              <Chip size="small" label={p.estado} color={estadoColor(p.estado)} />
            </TableCell>
            <TableCell align="right">{formatCurrency(p.importeEsperado)}</TableCell>
            <TableCell align="right">{formatCurrency(p.importeFacturadoNeto)}</TableCell>
            <TableCell align="right">{formatCurrency(p.importePendiente)}</TableCell>
            <TableCell>{p.nroFormularioFactura || '—'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

export default ContratoPeriodosTab;
